import { gql } from '@apollo/client';

import { UserInsightInterface } from '@generated-gql/generated-types';

const CAUTIONARY_TREATMENT_SLUGS = new Set([
  'RentAndLoans.new_loan',
  'CreditScore.BuildYourScore.new_account_in_foreclosure',
  'CreditScore.BuildYourScore.new_account_in_collections',
  'CreditScore.BuildYourScore.has_new_fraud',
  'CreditScore.BuildYourScore.late_accounts',
  'CreditScore.BuildYourScore.late_account',
  'CreditScore.BuildYourScore.new_delinquency',
  'CreditScore.BuildYourScore.account_is_delinquent',
  'top-takeaway-fee',
]);

const CELEBRATORY_TREATMENT_SLUGS = new Set([
  'CreditScore.BuildYourScore.derogatory_aged',
  'RentAndLoans.paid_off_loan',
  'CreditScore.CreditCards.paid_delinquent_account',
  'CreditScore.CreditCards.age_of_accounts_milestone',
  'CreditScore.CreditCards.total_utilization_decrease',
  'CreditScore.BuildYourScore.close_to_next_credit_bucket',
  'top-takeaway-deposit',
  'top-takeaway-net-savings-positive',
  'CreditScore.BuildYourScore.jumped_next_credit_band',
  'CreditScore.CreditCards.single_card_utilization_decrease',
  'CreditScore.CreditCards.utilization_decreased',
  'CreditScore.BuildYourScore.hard_inquiry_aged',
  'CreditScore.BuildYourScore.delinquency_aged',
]);

const PERSONALIZED_INSIGHT_SLUGS = new Set([
  'net-worth-increase-one-month',
  'net-worth-decrease-one-month',
  'CreditScore.BuildYourScore.has_score_to_use_credit_simulator',
  'top-takeaway-mm-grocery-spent-more',
  'top-takeaway-mm-shopping-spent-more',
  'top-takeaway-mm-utilities-spent-less',
  'top-takeaway-mm-fees-spent-less',
  'top-takeaway-mm-housing-spent-less',
  'top-takeaway-mm-subscription-spent-less',
  'top-takeaway-mm-grocery-spent-less',
  'top-takeaway-mm-shopping-spent-less',
  'net-worth-home-value-increase',
  'net-worth-home-value-decrease',
  'net-worth-greater-than-3x-monthly-spend',
  'RentAndLoans.new_loan',
  'CreditScore.CreditCards.opened_account',
  'CreditScore.BuildYourScore.avg_age_accounts_less_than_7_years',
  'CreditScore.BuildYourScore.new_installment_account',
  'CreditScore.BuildYourScore.new_revolving_account',
  'CreditScore.CreditCards.age_of_accounts_milestone',
  'CreditScore.BuildYourScore.jumped_next_credit_band',
  'CreditScore.BuildYourScore.new_account_in_foreclosure',
  'CreditScore.CreditCards.closed_account',
  'CreditScore.BuildYourScore.close_to_decreasing_credit_worthiness',
  'CreditScore.BuildYourScore.monthly_utilization_too_high',
  'CreditScore.BuildYourScore.close_to_next_credit_bucket',
  'CreditScore.BuildYourScore.new_account_in_collections',
  'CreditScore.BuildYourScore.derogatory_mark_expiring',
  'RentAndLoans.paid_off_loan',
  'CreditScore.CreditCards.paid_delinquent_account',
  'CreditScore.BuildYourScore.late_accounts',
  'CreditScore.BuildYourScore.late_account',
  'CreditScore.BuildYourScore.monthly_utilization_too_high',
  'CreditScore.CreditCards.increase_utilization_all_cards',
  'CreditScore.CreditCards.increase_utilization_single_card',
  'CreditScore.BuildYourScore.new_delinquency',
  'CreditScore.BuildYourScore.account_is_delinquent',
  'CreditScore.CreditCards.increased_limit',
  'CreditScore.CreditCards.utilization_decreased',
  'CreditScore.CreditCards.total_utilization_decrease',
  'CreditScore.CreditCards.single_card_utilization_decrease',
  'top-takeaway-update-income',
  'top-takeaway-fee',
  'top-takeaway-deposit',
  'top-takeaway-large-transaction',
  'top-takeaway-this-month-vs-last-month-spent-more',
  'top-takeaway-this-month-vs-last-month-spent-less',
  'top-takeaway-this-month-vs-last-month-spent-same',
  'top-takeaway-this-month-vs-last-month-spent-more-vis',
  'top-takeaway-this-month-vs-last-month-spent-less-vis',
  'top-takeaway-this-month-vs-last-month-spent-same-vis',
  'top-takeaway-bill-increase',
  'top-takeaway-bill-upcoming',
  'top-takeaway-net-savings-positive',
  'top-takeaway-net-savings-negative',
  'top-takeaway-mm-utilities-spent-more',
  'top-takeaway-mm-fees-spent-more',
  'top-takeaway-mm-housing-spent-more',
  'top-takeaway-mm-subscription-spent-more',
  'top-takeaway-top-freq-merchant-last-month',
  'top-takeaway-banking-fees-last-month',
  'top-takeaway-travel-spend-last-month-tu-not-connected',
  'top-takeaway-travel-spend-last-month-tu-connected',
  'cash-flow-spending-category-list',
  'cash-flow-spending-prev-month-category-other',
  'CreditBooster.ScoreBooster.pay_card_balance',
  'CreditBooster.ScoreBooster.increase_credit_limit',
  'CreditBooster.ScoreBooster.pay_only_past_due_to_normal',
  'CreditBooster.ScoreBooster.pay_min_past_due_to_normal',
  'CreditBooster.ScoreBooster.derog_acct_aged_off',
  'CreditBooster.ScoreBooster.open_card_account',
  'CreditBooster.ScoreBooster.member_home_data_viz',
  'CreditBooster.ScoreExplainer.sum_balances_increased',
  'CreditBooster.ScoreExplainer.sum_credit_card_balances_increased',
  'CreditBooster.ScoreExplainer.sum_credit_card_balances_decreased',
  'CreditBooster.ScoreExplainer.sum_credit_card_limits_increased',
  'CreditBooster.ScoreExplainer.sum_credit_card_limits_decreased',
  'CreditBooster.ScoreExplainer.percent_utilization_decreased',
  'CreditBooster.ScoreExplainer.percent_utilization_increased',
  'CreditBooster.ScoreExplainer.num_recent_inquiries_increased',
  'CreditBooster.ScoreExplainer.num_recent_inquiries_decreased',
  'CreditBooster.ScoreExplainer.num_derogatory_accounts_increased',
  'CreditBooster.ScoreExplainer.num_open_accounts_increased',
  'CreditBooster.ScoreExplainer.num_open_accounts_decreased',
  'CreditBooster.ScoreExplainer.num_delinquent_accounts_increased',
  'CreditBooster.ScoreExplainer.min_age_of_credit_increased',
  'CreditBooster.ScoreExplainer.sum_monthly_payments_increased',
  'CreditBooster.ScoreExplainer.sum_monthly_payments_decreased',
  'CreditBooster.ScoreExplainer.member_home_data_viz',
]);

const getIconographySentiment = (slug) => {
  if (CAUTIONARY_TREATMENT_SLUGS.has(slug)) return 'cautionary';
  if (CELEBRATORY_TREATMENT_SLUGS.has(slug)) return 'congratulatory';
  return 'none';
};

export const getSegmentPayload = (
  userInsight: UserInsightInterface,
  {
    elementPosition = 0,
    meaningfulEngagementCategory,
    moduleName,
    sectionPosition = 0,
  }: {
    elementPosition?: number;
    meaningfulEngagementCategory: string;
    moduleName: string;
    sectionPosition?: number;
  },
) => {
  const action = userInsight.actions?.[0];
  const appAction = action?.appAction;

  return {
    action_slug: action?.slug,
    cta: appAction?.text,
    data_visualization_type:
      userInsight?.dataVisualization?.dataVisualizationType?.toLowerCase() ??
      null,
    destination_url:
      appAction?.target ||
      (typeof window !== 'undefined' ? window.location.href : null),
    element_position: elementPosition,
    insight_iconography_sentiment: getIconographySentiment(userInsight.slug),
    insight_id: userInsight.id,
    insight_slug: userInsight.slug,
    is_personalized: PERSONALIZED_INSIGHT_SLUGS.has(userInsight.slug),
    meaningful_engagement_category: meaningfulEngagementCategory,
    meaningful_engagement_subcategory: 'UIA',
    meaningful_engagement_flag: true,
    module_name: moduleName,
    section_name: 'uia_module',
    section_position: sectionPosition,
    sentiment: userInsight.sentiment?.toLowerCase() ?? null,
    source_resource_id: userInsight.id,
  };
};
getSegmentPayload.fragment = gql`
  fragment getSegmentPayloadFragment on UserInsightInterface {
    actions {
      appAction {
        target
        text
      }
      slug
    }
    dataVisualization {
      dataVisualizationType
    }
    id
    sentiment
    slug
  }
`;
